import { Group, Paper, Stack, Switch, Text } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { ServiceNeedCardList } from "@/components/domain/care-plan/ServiceNeedCardList";
import {
  CLINICIAN_CONTACT_ONLY_DESCRIPTION,
  CLINICIAN_CONTACT_ONLY_LABEL,
  COMMUNICATION_HANDOFF_SECTION_TITLE,
  REFERRAL_APPROVAL_DESCRIPTION,
  REFERRAL_APPROVAL_LABEL,
  REFERRAL_FIELD_DESCRIPTION,
  REFERRAL_FIELD_LABEL,
  REFERRALS_SECTION_TITLE,
} from "@/components/domain/care-plan/enrollment-ui.constants";
import type { ServiceNeedFormItem } from "@benchmark/core/types";

/**
 * Props for shared referral service-needs fields.
 * @agentic connections
 */
export type ReferralServiceNeedsFieldsProps = {
  serviceNeeds: ServiceNeedFormItem[];
  requiresApproval: boolean;
  serviceNeedsError?: string | null;
  onServiceNeedsChange: (items: ServiceNeedFormItem[]) => void;
  onRequiresApprovalChange: (checked: boolean) => void;
  includeSection?: boolean;
  clinicianContactOnly?: boolean;
  onClinicianContactOnlyChange?: (checked: boolean) => void;
};

/**
 * Inline toggle row with label + description on the left and switch on the right.
 * @agentic connections
 */
const ToggleRow = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
    <Stack gap={2}>
      <Text size="sm" fw={600}>
        {label}
      </Text>
      <Text size="xs" c="dimmed">
        {description}
      </Text>
    </Stack>
    <Switch
      aria-label={label}
      checked={checked}
      onChange={(event) => onChange(event.currentTarget.checked)}
      styles={{ track: { flexShrink: 0 } }}
    />
  </Group>
);

/**
 * Shared UI fields for referral service-needs and approval settings.
 * @agentic connections
 */
export const ReferralServiceNeedsFields = ({
  serviceNeeds,
  requiresApproval,
  serviceNeedsError,
  onServiceNeedsChange,
  onRequiresApprovalChange,
  includeSection = false,
  clinicianContactOnly,
  onClinicianContactOnlyChange,
}: ReferralServiceNeedsFieldsProps) => {
  const content = (
    <Stack gap="lg">
      <FormField
        label={REFERRAL_FIELD_LABEL}
        required
        variant="section"
        showDivider={false}
        description={REFERRAL_FIELD_DESCRIPTION}
        error={serviceNeedsError ?? undefined}
      >
        <ServiceNeedCardList
          items={serviceNeeds}
          onChange={onServiceNeedsChange}
          showInlineErrors={!!serviceNeedsError}
        />
      </FormField>

      <Paper p="md" withBorder>
        <Stack gap="md">
          <Text size="sm" fw={700} tt="uppercase" c="dimmed">
            {COMMUNICATION_HANDOFF_SECTION_TITLE}
          </Text>

          {onClinicianContactOnlyChange !== undefined && (
            <ToggleRow
              label={CLINICIAN_CONTACT_ONLY_LABEL}
              description={CLINICIAN_CONTACT_ONLY_DESCRIPTION}
              checked={clinicianContactOnly ?? false}
              onChange={onClinicianContactOnlyChange}
            />
          )}

          <ToggleRow
            label={REFERRAL_APPROVAL_LABEL}
            description={REFERRAL_APPROVAL_DESCRIPTION}
            checked={requiresApproval}
            onChange={onRequiresApprovalChange}
          />
        </Stack>
      </Paper>
    </Stack>
  );

  if (!includeSection) {
    return content;
  }

  return (
    <FormSection title={REFERRALS_SECTION_TITLE} showDivider={false}>
      {content}
    </FormSection>
  );
};
